import { TrashIcon, ExclamationTriangleIcon, EyeIcon, EyeSlashIcon, PencilIcon } from '@heroicons/react/24/solid';
import type { FormEvent, ChangeEvent } from 'react';
import { useState, useEffect, useMemo } from 'react';
import React from 'react';
import { gradeApi } from '../utils/api';
import { useUser } from '../contexts/UserContext';

interface Grade {
  id: string;
  name: string;
  description?: string;
  level: number;
  code: string;
  roleName: string;
  active: boolean;
  createdDate: string;
  modificationDate: string;
  subjectCount: number;
}

interface FormData {
  name: string;
  description: string;
  level: string;
  code: string;
  roleName: string;
  active: boolean;
}

interface SortConfig {
  key: keyof Grade;
  direction: 'asc' | 'desc';
}

const Grades = () => {
  const { user, isModerator } = useUser();
  const [showAddForm, setShowAddForm] = useState(false);
  const [grades, setGrades] = useState<Grade[]>([]);
  const [filters, setFilters] = useState({
    gradeName: '',
    level: '',
    active: '',
  });
  const [displayedGrades, setDisplayedGrades] = useState<Grade[]>([]);
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    description: '',
    level: '',
    code: '',
    roleName: '',
    active: true,
  });
  const [editingGrade, setEditingGrade] = useState<Grade | null>(null);
  const [gradeToDelete, setGradeToDelete] = useState<Grade | null>(null);
  const [gradeToToggle, setGradeToToggle] = useState<Grade | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch grades from API
  const fetchGrades = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await gradeApi.getGrades();
      
      if (response.success) {
        // Map API response to our Grade interface
        const formattedGrades: Grade[] = response.data.map((grade: any) => ({
          id: grade.id,
          name: grade.name,
          description: grade.description || '',
          level: grade.level,
          code: grade.code || '',
          roleName: grade.roleName || '',
          active: grade.active,
          createdDate: grade.createdDate,
          modificationDate: grade.modificationDate,
          subjectCount: grade.subjectCount || 0,
        }));
        
        setGrades(formattedGrades);
        setDisplayedGrades(formattedGrades);
      } else {
        throw new Error(response.message || 'Failed to fetch grades');
      }
    } catch (error) {
      setGrades([]);
      setDisplayedGrades([]);
      setError('Failed to fetch grades. Please try again later.');
      console.error('Error fetching grades:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGrades();
  }, []);

  // Apply filters
  useEffect(() => {
    let filtered = [...grades];

    if (filters.gradeName) {
      filtered = filtered.filter(grade =>
        grade.name.toLowerCase().includes(filters.gradeName.toLowerCase())
      );
    }

    if (filters.level) {
      filtered = filtered.filter(grade => grade.level.toString() === filters.level);
    }

    if (filters.active !== '') {
      const isActive = filters.active === 'true';
      filtered = filtered.filter(grade => grade.active === isActive);
    }

    setDisplayedGrades(filtered);
  }, [filters, grades]);

  // Sort function
  const handleSort = (key: keyof Grade) => {
    setSortConfig((prevSortConfig) => {
      const direction = prevSortConfig?.key === key && prevSortConfig.direction === 'asc' ? 'desc' : 'asc';
      return { key, direction };
    });
  };

  // Apply sorting to the displayed data
  const sortedGrades = useMemo(() => {
    if (!sortConfig) return displayedGrades;
    
    return [...displayedGrades].sort((a, b) => {
      const key = sortConfig.key;
      const direction = sortConfig.direction;
      
      const aValue = a[key];
      const bValue = b[key];
      
      // Handle undefined values
      if (aValue === undefined || aValue === null) return 1;
      if (bValue === undefined || bValue === null) return -1;
      
      if (aValue < bValue) {
        return direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [displayedGrades, sortConfig]);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData((prev) => {
      if (name === 'active') {
        return { ...prev, [name]: value === 'true' };
      } else if (name === 'level') {
        return { ...prev, [name]: value };
      } else {
        return { ...prev, [name]: value };
      }
    });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    try {
      let response;
      if (editingGrade) {
        // Update existing grade
        response = await gradeApi.updateGrade(editingGrade.id, {
          id: editingGrade.id,
          name: formData.name,
          description: formData.description || undefined,
          level: parseInt(formData.level),
          active: formData.active,
        });
      } else {
        // Create new grade
        const addData: any = {
          name: formData.name,
          description: formData.description || undefined,
          level: parseInt(formData.level),
          active: formData.active,
        };
        
        if (formData.code) {
          addData.code = formData.code;
        }
        
        if (formData.roleName) {
          addData.roleName = formData.roleName;
        }
        
        response = await gradeApi.createGrade(addData);
      }
      
      if (response.success) {
        setShowAddForm(false);
        setEditingGrade(null);
        setFormData({
          name: '',
          description: '',
          level: '',
          code: '',
          roleName: '',
          active: true,
        });
        fetchGrades();
      } else {
        alert('Failed to ' + (editingGrade ? 'update' : 'create') + ' grade: ' + response.message);
      }
    } catch (error) {
      console.error('Error saving grade:', error);
      alert('Failed to save grade. Please try again later.');
    }
  };

  const handleEdit = (grade: Grade) => {
    setEditingGrade(grade);
    setFormData({
      name: grade.name,
      description: grade.description || '',
      level: grade.level.toString(),
      code: grade.code || '',
      roleName: grade.roleName || '',
      active: grade.active,
    });
    setShowAddForm(true);
  };

  const handleCancel = () => {
    setShowAddForm(false);
    setEditingGrade(null);
    setFormData({
      name: '',
      description: '',
      level: '',
      code: '',
      roleName: '',
      active: true,
    });
  };

  const handleDelete = async (id: string) => {
    setIsDeleting(true);
    
    try {
      const response = await gradeApi.deleteGrade(id);
      
      if (response.success) {
        setGrades(grades.filter((grade: Grade) => grade.id !== id));
        setDisplayedGrades(displayedGrades.filter((grade: Grade) => grade.id !== id));
        setGradeToDelete(null);
      } else {
        alert('Failed to delete grade: ' + response.message);
      }
    } catch (error) {
      console.error('Error deleting grade:', error);
      alert('Failed to delete grade. Please try again later.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleToggleStatus = async (grade: Grade) => {
    try {
      const newActive = !grade.active;
      
      const response = await gradeApi.updateGrade(grade.id, {
        id: grade.id,
        name: grade.name,
        description: grade.description || undefined,
        level: grade.level,
        active: newActive,
      });
      
      if (response.success) {
        // Update local state
        setGrades(
          grades.map((g: Grade) =>
            g.id === grade.id
              ? {
                  ...g,
                  active: newActive,
                }
              : g
          )
        );
        setDisplayedGrades(
          displayedGrades.map((g: Grade) =>
            g.id === grade.id
              ? {
                  ...g,
                  active: newActive,
                }
              : g
          )
        );
        setGradeToToggle(null);
      } else {
        alert('Failed to update grade status: ' + response.message);
      }
    } catch (error) {
      console.error('Error updating grade status:', error);
      alert('Failed to update grade status. Please try again later.');
    }
  };

  const handleSearch = () => {
    // Filters are applied automatically via useEffect
  };

  const handleResetFilters = () => {
    setFilters({
      gradeName: '',
      level: '',
      active: '',
    });
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('ar-EG', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100" dir="rtl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
                السنين الدراسية
              </h1>
              <p className="mt-2 text-gray-600">إدارة السنين الدراسية والصفوف</p>
            </div>
            {isModerator() && (
              <button
                onClick={() => {
                  setEditingGrade(null);
                  setFormData({
                    name: '',
                    description: '',
                    level: '',
                    code: '',
                    roleName: '',
                    active: true,
                  });
                  setShowAddForm(true);
                }}
                className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm rounded-lg 
                           shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 focus:outline-none focus:ring-2 
                           focus:ring-offset-2 focus:ring-blue-500"
              >
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                إضافة سنة دراسية جديدة
              </button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8 transition-all duration-200 hover:shadow-xl">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            <div className="space-y-1">
              <label htmlFor="name-filter" className="block text-sm font-medium text-gray-700">
                اسم السنة الدراسية
              </label>
              <input
                type="text"
                id="name-filter"
                value={filters.gradeName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, gradeName: e.target.value })}
                className="block w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent
                         transition-colors duration-200"
                placeholder="البحث في السنين الدراسية..."
              />
            </div>
            <div className="space-y-1">
              <label htmlFor="level-filter" className="block text-sm font-medium text-gray-700">
                المستوى
              </label>
              <input
                type="number"
                id="level-filter"
                min="1"
                max="12"
                value={filters.level}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, level: e.target.value })}
                className="block w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent
                         transition-colors duration-200"
                placeholder="المستوى..."
              />
            </div>
            <div className="space-y-1">
              <label htmlFor="status-filter" className="block text-sm font-medium text-gray-700">
                الحالة
              </label>
              <select
                id="status-filter"
                value={filters.active}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setFilters({ ...filters, active: e.target.value })}
                className="block w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent
                         transition-colors duration-200"
              >
                <option value="">جميع الحالات</option>
                <option value="true">نشط</option>
                <option value="false">غير نشط</option>
              </select>
            </div>
          </div>
          <div className="mt-6 flex justify-end gap-4">
            <button
              onClick={handleSearch}
              className="inline-flex items-center px-6 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm rounded-lg
                       shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 focus:outline-none focus:ring-2
                       focus:ring-offset-2 focus:ring-blue-500"
            >
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              تطبيق التصفية
            </button>
            <button
              onClick={handleResetFilters}
              className="inline-flex items-center px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 text-sm transition-colors duration-200"
            >
              إعادة تعيين
            </button>
          </div>
        </div>

        {/* Grades Table */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden transition-all duration-200 hover:shadow-xl">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr className="bg-gradient-to-r from-gray-50 to-gray-100">
                  <th 
                    scope="col" 
                    className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-200 transition-colors"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center justify-end">
                      اسم السنة الدراسية
                      {sortConfig?.key === 'name' && (
                        <span className="mr-2">
                          {sortConfig.direction === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th 
                    scope="col" 
                    className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-200 transition-colors"
                    onClick={() => handleSort('level')}
                  >
                    <div className="flex items-center justify-end">
                      المستوى
                      {sortConfig?.key === 'level' && (
                        <span className="mr-2">
                          {sortConfig.direction === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th 
                    scope="col" 
                    className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-200 transition-colors"
                    onClick={() => handleSort('subjectCount')}
                  >
                    <div className="flex items-center justify-end">
                      عدد المواد
                      {sortConfig?.key === 'subjectCount' && (
                        <span className="mr-2">
                          {sortConfig.direction === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th 
                    scope="col" 
                    className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-200 transition-colors"
                    onClick={() => handleSort('active')}
                  >
                    <div className="flex items-center justify-end">
                      الحالة
                      {sortConfig?.key === 'active' && (
                        <span className="mr-2">
                          {sortConfig.direction === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th 
                    scope="col" 
                    className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-200 transition-colors"
                    onClick={() => handleSort('modificationDate')}
                  >
                    <div className="flex items-center justify-end">
                      آخر تعديل
                      {sortConfig?.key === 'modificationDate' && (
                        <span className="mr-2">
                          {sortConfig.direction === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  {isModerator() && (
                    <th scope="col" className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      الإجراءات
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={isModerator() ? 6 : 5} className="px-6 py-12">
                      <div className="flex justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      </div>
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td colSpan={isModerator() ? 6 : 5} className="px-6 py-4">
                      <div className="flex items-center justify-center text-red-500">
                        <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {error}
                      </div>
                    </td>
                  </tr>
                ) : sortedGrades.length === 0 ? (
                  <tr>
                    <td colSpan={isModerator() ? 6 : 5} className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center justify-center text-gray-500">
                        <svg className="w-12 h-12 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0118 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
                        </svg>
                        <p className="text-lg font-medium">لم يتم العثور على سنوات دراسية</p>
                        <p className="text-sm">حاول تعديل التصفية للعثور على ما تبحث عنه</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  sortedGrades.map((grade) => (
                    <tr
                      key={grade.id}
                      className="hover:bg-gray-50 transition-colors duration-150"
                    >
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{grade.name}</div>
                        {grade.description && (
                          <div className="text-xs text-gray-500 mt-1">{grade.description}</div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-600">{grade.level}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-600">{grade.subjectCount}</div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                          ${grade.active 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'}`}
                        >
                          {grade.active ? 'نشط' : 'غير نشط'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-600">{formatDate(grade.modificationDate)}</div>
                      </td>
                      {isModerator() && (
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end space-x-reverse space-x-2">
                            <button
                              onClick={() => handleEdit(grade)}
                              className="inline-flex items-center p-2 text-blue-600 hover:text-blue-900 rounded-lg hover:bg-blue-50
                                       transition-colors duration-200"
                              title="تعديل السنة الدراسية"
                            >
                              <PencilIcon className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => setGradeToToggle(grade)}
                              className={`inline-flex items-center p-2 rounded-lg transition-colors duration-200 ${
                                grade.active
                                  ? 'text-yellow-600 hover:text-yellow-900 hover:bg-yellow-50'
                                  : 'text-green-600 hover:text-green-900 hover:bg-green-50'
                              }`}
                              title={grade.active ? 'إخفاء السنة الدراسية' : 'إظهار السنة الدراسية'}
                            >
                              {grade.active ? (
                                <EyeSlashIcon className="h-5 w-5" />
                              ) : (
                                <EyeIcon className="h-5 w-5" />
                              )}
                            </button>
                            <button
                              onClick={() => setGradeToDelete(grade)}
                              className="inline-flex items-center p-2 text-red-600 hover:text-red-900 rounded-lg hover:bg-red-50
                                       transition-colors duration-200"
                              title="حذف السنة الدراسية"
                            >
                              <TrashIcon className="h-5 w-5" />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Add/Edit Grade Form Modal */}
        {showAddForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
              <h2 className="text-xl sm:text-2xl font-bold mb-6">
                {editingGrade ? 'تعديل السنة الدراسية' : 'إضافة سنة دراسية جديدة'}
              </h2>
              <form onSubmit={handleSubmit}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-2">
                      اسم السنة الدراسية <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={handleInputChange}
                      name="name"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                      placeholder="أدخل اسم السنة الدراسية"
                      maxLength={100}
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-2">
                      الوصف
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={handleInputChange}
                      name="description"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      rows={3}
                      placeholder="أدخل وصف السنة الدراسية (اختياري)"
                      maxLength={500}
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-2">
                      المستوى <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      value={formData.level}
                      onChange={handleInputChange}
                      name="level"
                      min="1"
                      max="12"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                      placeholder="المستوى (1-12)"
                    />
                  </div>
                  {!editingGrade && (
                    <>
                      <div>
                        <label className="block text-gray-700 text-sm font-bold mb-2">
                          الكود
                        </label>
                        <input
                          type="text"
                          value={formData.code}
                          onChange={handleInputChange}
                          name="code"
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="الكود (سيتم توليده تلقائياً إذا لم يتم إدخاله)"
                          maxLength={50}
                        />
                        <p className="text-xs text-gray-500 mt-1">سيتم توليده تلقائياً إذا لم يتم إدخاله</p>
                      </div>
                      <div>
                        <label className="block text-gray-700 text-sm font-bold mb-2">
                          اسم الدور
                        </label>
                        <input
                          type="text"
                          value={formData.roleName}
                          onChange={handleInputChange}
                          name="roleName"
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="اسم الدور (سيتم توليده تلقائياً إذا لم يتم إدخاله)"
                          maxLength={20}
                        />
                        <p className="text-xs text-gray-500 mt-1">سيتم توليده تلقائياً إذا لم يتم إدخاله</p>
                      </div>
                    </>
                  )}
                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-2">
                      الحالة
                    </label>
                    <select
                      value={formData.active ? 'true' : 'false'}
                      onChange={handleInputChange}
                      name="active"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="true">نشط</option>
                      <option value="false">غير نشط</option>
                    </select>
                  </div>
                </div>
                <div className="flex justify-end gap-4 mt-6">
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    إلغاء
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-colors"
                  >
                    {editingGrade ? 'حفظ التعديلات' : 'إضافة السنة الدراسية'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {gradeToDelete && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md border border-gray-200 transform transition-all">
              <div className="text-center mb-5">
                <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
                  <TrashIcon className="h-8 w-8 text-red-600" aria-hidden="true" />
                </div>
                <h3 className="text-xl font-medium text-gray-900 mb-2">حذف السنة الدراسية</h3>
                <p className="text-gray-600">
                  هل أنت متأكد من حذف <span className="font-bold text-gray-900">{gradeToDelete.name}</span>؟
                  {gradeToDelete.subjectCount > 0 && (
                    <span className="block mt-2 text-red-600 font-medium">
                      تحذير: تحتوي هذه السنة الدراسية على {gradeToDelete.subjectCount} مادة. لا يمكن حذفها.
                    </span>
                  )}
                  {gradeToDelete.subjectCount === 0 && (
                    <span className="block mt-2 text-gray-500">لا يمكن التراجع عن هذا الإجراء.</span>
                  )}
                </p>
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 font-medium hover:bg-gray-200 transition-all focus:outline-none focus:ring-2 focus:ring-gray-300"
                  onClick={() => setGradeToDelete(null)}
                  disabled={isDeleting}
                >
                  إلغاء
                </button>
                <button
                  className="px-4 py-2 rounded-lg bg-red-600 text-white font-medium hover:bg-red-700 transition-all focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-70 flex items-center gap-2 disabled:cursor-not-allowed"
                  onClick={() => gradeToDelete.subjectCount === 0 && handleDelete(gradeToDelete.id)}
                  disabled={isDeleting || gradeToDelete.subjectCount > 0}
                >
                  {isDeleting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      جاري الحذف...
                    </>
                  ) : (
                    <>
                      <TrashIcon className="h-4 w-4" />
                      حذف
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Status Change Confirmation Modal */}
        {gradeToToggle && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md border border-gray-200 transform transition-all">
              <div className="text-center mb-5">
                <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-blue-100 mb-4">
                  <ExclamationTriangleIcon className="h-8 w-8 text-blue-600" aria-hidden="true" />
                </div>
                <h3 className="text-xl font-medium text-gray-900 mb-2">
                  {gradeToToggle.active ? 'إخفاء السنة الدراسية' : 'إظهار السنة الدراسية'}
                </h3>
                <p className="text-gray-600">
                  هل أنت متأكد من أنك تريد {gradeToToggle.active ? 'إخفاء' : 'إظهار'} السنة الدراسية <span className="font-bold text-gray-900">{gradeToToggle.name}</span>؟
                </p>
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 font-medium hover:bg-gray-200 transition-all focus:outline-none focus:ring-2 focus:ring-gray-300"
                  onClick={() => setGradeToToggle(null)}
                >
                  إلغاء
                </button>
                <button
                  className={`px-4 py-2 rounded-lg ${
                    gradeToToggle.active
                      ? 'bg-yellow-600 hover:bg-yellow-700'
                      : 'bg-green-600 hover:bg-green-700'
                  } text-white font-medium transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                    gradeToToggle.active
                      ? 'focus:ring-yellow-500'
                      : 'focus:ring-green-500'
                  }`}
                  onClick={() => handleToggleStatus(gradeToToggle)}
                >
                  {gradeToToggle.active ? 'إخفاء السنة الدراسية' : 'إظهار السنة الدراسية'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Grades;
